import { Link } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { DownloadsTray } from "@/components/photos/DownloadsTray";
import { MadisonLogo } from "@/components/shared/madison-logo";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { describeAccess } from "@/lib/access";
import { useSession } from "@/lib/auth-client";
import { Sidebar } from "./Sidebar";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { ThemeToggle } from "./ThemeToggle";

function DashboardLayoutInner({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const { isMobile, setMobileOpen } = useSidebar();

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {/* Top bar: navy in both themes */}
      <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b-2 border-(--brand-highlight) bg-(--brand-primary) px-4 text-(--fg-inverse) md:px-6">
        <div className="flex shrink-0 items-center gap-3">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen(true)}
              className="shrink-0 text-(--fg-inverse) hover:bg-white/10 hover:text-(--fg-inverse)"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          {/* Madison's white logo on the navy bar, as in MESH 1; the hex mark alone on phones. */}
          <Link to="/dashboard" className="flex shrink-0 items-center gap-3 whitespace-nowrap">
            <MadisonLogo tone="white" className="hidden h-8 sm:block" />
            <MadisonLogo variant="mark" tone="white" className="h-8 sm:hidden" />
            <span aria-hidden="true" className="hidden h-6 w-px bg-white/30 sm:block" />
            <span className="font-display text-base font-semibold sm:text-lg">Photo Portal</span>
          </Link>
        </div>
        <div className="flex min-w-0 items-center gap-3">
          {session?.user && (
            <span
              data-testid="access-label"
              title={describeAccess(session)}
              className="truncate rounded-full bg-(--brand-highlight) px-3 py-0.5 text-xs font-semibold text-(--brand-primary)"
            >
              {describeAccess(session)}
            </span>
          )}
          <ThemeToggle className="hidden shrink-0 hover:bg-white/10 hover:text-(--fg-inverse) sm:inline-flex" />
          {session?.user && (
            <Link
              to="/dashboard/profile"
              title="Profile"
              className="shrink-0 rounded-full ring-offset-background transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <UserAvatar
                user={session.user}
                className="h-8 w-8 bg-(--fg-inverse) text-(--brand-primary)"
              />
            </Link>
          )}
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Sidebar />

        {/* Scrollable content. Full width: grids here are tables that need the room. */}
        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">{children}</main>
      </div>
      <DownloadsTray />
    </div>
  );
}

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <DashboardLayoutInner>{children}</DashboardLayoutInner>
    </SidebarProvider>
  );
}
